import { useEffect, useState } from 'react';
import { FilePlus, LayoutDashboard, Loader2 } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';
import { GuardedModal, useDialogs } from '@onvice/portal-ui';

/**
 * Anlegen-Dialog fuer eine neue persoenliche Uebersicht-Seite.
 *
 * Optionen: leer starten oder aus einer Vorlage. Vorlagen sind sortiert nach
 * fits_me (suggested_for_groups schneidet sich mit Gruppen des Users), dann
 * nach sort_order. Onvice-Vorlagen (tenant_id=NULL) und Mandant-Vorlagen sind
 * sichtbar gemischt; is_mandant_template=true bedeutet mandant-eigene.
 *
 * Konzept: 04 Ressourcen/Softwarearchitektur/Widget-Picker-Konzept.md
 *
 * Dokument-Navigation (Abhakliste 2.94, 2026-10-01): Dialog-Schutz ab dem Schritt „Name"
 * (Klick daneben, X und Escape fragen nach); Pflichtfeld und Fehler als Meldung.
 */

interface Template {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  sort_order: number;
  suggested_for_groups: string[];
  fits_me: boolean;
  is_mandant_template: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Wird mit der ID der neu erstellten Seite aufgerufen — Parent navigiert hin. */
  onCreated: (newPageId: string) => void;
  /** Admin-Mode (Joerg 2026-06-08): wenn gesetzt, laeuft POST gegen
   *  /admin/users/:actAsUserId/pages bzw. /from-template, sodass die Seite im
   *  Auftrag des Ziel-Users angelegt wird. Templates-Liste bleibt gleich
   *  (Vorlagen sind tenant-weit, nicht user-spezifisch). */
  actAsUserId?: string;
}

export default function NewPageDialog({ open, onClose, onCreated, actAsUserId }: Props) {
  const basePath = actAsUserId
    ? `/portal/admin/users/${actAsUserId}/pages`
    : '/portal/my-pages';
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [step, setStep] = useState<'choose' | 'name'>('choose');
  const [picked, setPicked] = useState<Template | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const dlg = useDialogs();

  useEffect(() => {
    if (!open) return;
    setStep('choose');
    setPicked(null);
    setName('');
    api
      .get<{ success: boolean; data: Template[] }>('/portal/page-templates')
      .then((r) => { if (r.success) setTemplates(r.data); })
      .catch(() => setTemplates([]));
  }, [open]);

  const onPick = (t: Template | null) => {
    setPicked(t);
    setName(t?.name ?? 'Meine Übersicht');
    setStep('name');
  };

  const onConfirm = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      await dlg.notify({ kind: 'error', title: 'Seite kann nicht angelegt werden', message: 'Bitte zuerst ergänzen:', items: ['Name der Seite'] });
      return;
    }
    setBusy(true);
    try {
      if (picked) {
        const r = await api.post<{ success: boolean; data: { id: string } }>(
          `${basePath}/from-template`,
          { template_id: picked.id, name_override: trimmed },
        );
        if (!r.success) throw new Error('Anlegen fehlgeschlagen');
        onCreated(r.data.id);
      } else {
        const r = await api.post<{ success: boolean; data: { id: string } }>(
          basePath,
          { name: trimmed, icon: 'LayoutDashboard', layout: [] },
        );
        if (!r.success) throw new Error('Anlegen fehlgeschlagen');
        onCreated(r.data.id);
      }
    } catch (e) {
      await dlg.error('Seite wurde nicht angelegt', e);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <GuardedModal
      open
      title="Neue Übersicht-Seite"
      label="Neue Seite"
      dirty={step === 'name'}
      busy={busy}
      onClose={onClose}
      size="lg"
      testAnchor="new-page"
      footer={step === 'name' ? (requestClose) => (
        <>
          <button type="button" onClick={() => setStep('choose')} className="text-sm text-muted-foreground hover:text-foreground px-3 py-2 min-h-[44px] sm:mr-auto">← Zurück</button>
          <button type="button" onClick={requestClose} disabled={busy} className="px-3 py-2 rounded-lg border border-default text-foreground hover:bg-muted text-sm min-h-[44px]">Abbrechen</button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={busy || !name.trim()}
            data-action="create"
            className="px-4 py-2 rounded-lg bg-brand-600 text-primary-foreground hover:bg-brand-500 text-sm disabled:opacity-50 min-h-[44px]"
          >
            {busy ? <Loader2 size={14} className="inline animate-spin mr-1" /> : null}
            Anlegen
          </button>
        </>
      ) : undefined}
    >
        {step === 'choose' && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Starte mit einer Vorlage oder lege eine leere Seite an, die du selbst zusammenstellst.
            </p>

            {templates === null ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin text-muted-foreground" /></div>
            ) : (
              <>
                <button
                  onClick={() => onPick(null)}
                  className="w-full flex items-start gap-3 p-3 rounded-lg border border-default hover:bg-muted text-left"
                >
                  <FilePlus size={20} className="text-muted-foreground shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-sm">Leere Seite</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Widgets später über „Layout anpassen" hinzufügen.</div>
                  </div>
                </button>

                {templates.filter((t) => t.fits_me).length > 0 && (
                  <div className="pt-2">
                    <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Für dich empfohlen</div>
                    <div className="space-y-2">
                      {templates.filter((t) => t.fits_me).map((t) => (
                        <TemplateRow key={t.id} template={t} onPick={() => onPick(t)} />
                      ))}
                    </div>
                  </div>
                )}

                {templates.filter((t) => !t.fits_me).length > 0 && (
                  <div className="pt-2">
                    <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Weitere Vorlagen</div>
                    <div className="space-y-2">
                      {templates.filter((t) => !t.fits_me).map((t) => (
                        <TemplateRow key={t.id} template={t} onPick={() => onPick(t)} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {step === 'name' && (
          <div className="space-y-3">
            <div className="rounded-lg bg-muted/50 p-3 text-sm">
              {picked ? (
                <>
                  <div className="font-medium">Vorlage: {picked.name}</div>
                  {picked.description && <div className="text-xs text-muted-foreground mt-1">{picked.description}</div>}
                </>
              ) : (
                <div className="font-medium">Leere Seite</div>
              )}
            </div>
            <label className="block">
              <div className="text-sm font-medium mb-1">Name der Seite</div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                className="w-full px-3 py-2 rounded-lg border border-default bg-card text-foreground"
                placeholder="z.B. Meine Übersicht"
                data-test-anchor="new-page-name"
              />
            </label>
          </div>
        )}
    </GuardedModal>
  );
}

function TemplateRow({ template, onPick }: { template: Template; onPick: () => void }) {
  return (
    <button
      onClick={onPick}
      className="w-full flex items-start gap-3 p-3 rounded-lg border border-default hover:bg-muted text-left"
    >
      <LayoutDashboard size={20} className="text-muted-foreground shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{template.name}</span>
          {template.is_mandant_template && (
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted px-1.5 py-0.5 rounded">Eigene Vorlage</span>
          )}
        </div>
        {template.description && (
          <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{template.description}</div>
        )}
      </div>
    </button>
  );
}
